import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PowerBIEmbed } from 'powerbi-client-react';
import { Embed, models, Report, service } from 'powerbi-client';
import apiClient from '../services/api';
import { useReportState, type IPowerBISlicerConfig } from '../context/ReportStateContext';
import './spinner.css';

const isSlicerLike = (v: { type?: string; name?: string }) => {
  const t = String(v?.type || '').toLowerCase();
  return t === 'slicer' || t.startsWith('inforiverfilter') || t.includes('slicer');
};

const ReportEmbed = () => {
  const { reportId } = useParams();
  const { getReportSlicers, saveReportSlicers } = useReportState();

  const reportRef = useRef<Report | undefined>(undefined);

  // NEW: track whether we already applied saved slicers for this embed
  const appliedOnceRef = useRef(false);

  const reportIdRef = useRef(reportId);
  useEffect(() => { reportIdRef.current = reportId; }, [reportId]);

  // NEW: flag to avoid capturing right after we programmatically apply slicers
  const isApplyingRef = useRef(false);

  const [embedConfig, setEmbedConfig] = useState<models.IReportEmbedConfiguration>({
    type: 'report',
    embedUrl: undefined,
    tokenType: models.TokenType.Embed,
    accessToken: undefined,
    settings: undefined,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<number | undefined>(undefined);
  const isResettingRef = useRef(false);
  const [embedKey, setEmbedKey] = useState(0);

  /** ---------- Helpers ---------- */

  // Avoid applying multiple states for the same target/sync-group
  const dedupeByTarget = (slicers: IPowerBISlicerConfig[]) => {
    const seen = new Set<string>();
    const result: IPowerBISlicerConfig[] = [];

    for (const s of slicers) {
      const filter = s.state?.filters?.[0];
      const target = (filter as models.IFilter)?.target ?? s.state?.targets?.[0];

      let key = (s.selector as models.IVisualSelector).visualName;

      if (target) {
        interface TargetProps {
          table?: string;
          column?: string;
          hierarchy?: string;
          hierarchyLevel?: string;
        }
        const t = target as TargetProps;
        key = `${t.table}|${t.column || t.hierarchy || ''}|${t.hierarchyLevel || ''}`;
      }

      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push(s);
    }
    return result;
  };

  // Apply saved slicers after report has rendered
  // logic: Apply ONLY if it's NOT a known native slicer (those go to embedConfig)
  const applyPostRenderSlicers = useCallback(async (currentReport: Report, currentReportId: string) => {
    try {
      const saved = getReportSlicers(currentReportId) ?? [];
      if (!saved.length) return;

      const pages = await currentReport.getPages();
      const active = pages.find(p => p.isActive) ?? pages[0];
      const visuals = await active.getVisuals();

      console.debug(`5. Found ${visuals.length} visuals on active page.`);

      // Map saved slicers to actual slicer-like visuals on the page
      const toApply = saved
        .filter(s => {
          // If explicitly marked as native slicer, skip (handled by embedConfig)
          if (s.visualType?.toLowerCase().includes('slicer')) return false;

          // Otherwise (custom visual OR legacy saved state without type), try to find and apply
          const visualName = (s.selector as models.IVisualSelector).visualName;
          const v = visuals.find(v => v.name === visualName);
          if (v && isSlicerLike(v as { type?: string; name?: string })) return true;

          return false;
        });

      if (toApply.length === 0) return;

      const unique = dedupeByTarget(toApply);
      console.debug(`5a. Applying ${unique.length} custom/legacy slicer states post-render.`);

      // Delay to ensure custom visual initialized its objects
      await new Promise(r => setTimeout(r, 350));
      isApplyingRef.current = true;

      for (const s of unique) {
        const visualName = (s.selector as models.IVisualSelector).visualName;
        const v = visuals.find(v => v.name === visualName);
        if (!v) continue;
        try {
          console.debug(`5b. Restoring state for visual ${v.name}`);
          await v.setSlicerState(s.state);
        } catch (err) {
          console.warn('5c. First attempt failed; retrying slicer set', v.name, err);
          await new Promise(r => setTimeout(r, 600));
          const v2 = visuals.find(vx => vx.name === visualName);
          if (v2) await v2.setSlicerState(s.state);
        }
      }
    } catch (err) {
      console.error('5d. applyPostRenderSlicers error:', err);
    } finally {
      setTimeout(() => { isApplyingRef.current = false; }, 250);
    }
  }, [getReportSlicers]);

  /** ---------- Capture ---------- */

  const captureSlicerState = useCallback(async (currentReport: Report, captureForReportId: string) => {
    if (currentReport !== reportRef.current) return;
    try {
      const pages = await currentReport.getPages();
      const slicerConfigs: IPowerBISlicerConfig[] = [];

      for (const page of pages) {
        const visuals = await page.getVisuals();
        for (const visual of visuals) {
          if (isSlicerLike(visual as { type?: string; name?: string })) {
            try {
              const state = await visual.getSlicerState();
              if (state) {
                slicerConfigs.push({
                  selector: {
                    $schema: "http://powerbi.com/product/schema#visualSelector",
                    visualName: visual.name,
                  },
                  state,
                  visualType: visual.type // Save type to distinguish native vs custom
                });
              }
            } catch (visualError) {
              console.debug(`7a. Failed to get state for slicer-like visual ${visual.name}:`, visualError);
            }
          }
        }
      }

      if (slicerConfigs.length > 0) {
        console.debug("7. Saving slicer configs:", slicerConfigs, " for report: ", captureForReportId);
        const deduped = dedupeByTarget(slicerConfigs);
        saveReportSlicers(captureForReportId, deduped);
      }
    } catch (e) {
      if (!e) {
        console.debug("7b. Capture failed silently (likely due to report unmount/navigation).");
      } else {
        console.error("7b. Error capturing slicer state:", e);
      }
    }
  }, [saveReportSlicers]);

  const debouncedCapture = useCallback(() => {
    // Guard: Do not capture if we haven't finished the initial render/apply cycle
    if (!appliedOnceRef.current) {
      console.debug('6. Skipping capture: Report not fully initialized/restored yet (appliedOnceRef=false).');
      return;
    }

    const scheduledForReportId = reportIdRef.current;
    console.debug('6a. Scheduling state capture for report:', scheduledForReportId);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = window.setTimeout(() => {
      const report = reportRef.current;
      if (!report || !scheduledForReportId) return;

      // Skip capture if we just applied programmatically
      if (isApplyingRef.current) {
        console.debug('6b. Skipping capture: isApplyingRef active');
        return;
      }

      captureSlicerState(report, scheduledForReportId);
    }, 500);
  }, [captureSlicerState]);

  /** ---------- Reset (unchanged) ---------- */

  const handleReset = useCallback(async () => {
    const rid = reportIdRef.current;
    if (!rid || !reportRef.current) return;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = undefined;
    }
    isResettingRef.current = true;
    saveReportSlicers(rid, []);
    // Do not pass slicers at load; we re-embed cleanly
    setEmbedConfig(prev => ({ ...prev, slicers: [] }));
    setEmbedKey(prev => prev + 1);
    console.debug('8. Resetting report to default state.');
  }, [saveReportSlicers, setEmbedConfig]);

  /** ---------- Unmount flush (unchanged) ---------- */

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = undefined;
        if (reportRef.current && reportId) {
          console.log("9. Flushing pending slicer capture for report", reportId);
          captureSlicerState(reportRef.current, reportId).catch(e => console.warn("Failed to flush slicer state on unmount", e));
        }
      }
    };
  }, [reportId, captureSlicerState]);

  /** ---------- Fetch embed config (changed: do NOT pass slicers at load) ---------- */

  useEffect(() => {
    const fetchEmbedConfig = async () => {
      setIsLoading(true);
      // setIsRendered(false); // Removed
      setError(null);
      // setReport(undefined); // Removed
      reportRef.current = undefined;

      if (!reportId) {
        setIsLoading(false);
        return;
      }
      try {
        console.debug("1. Fetching embed config...");
        const response = await apiClient.get(`/report/embed-config?report_id=${reportId}`);

        // Retrieve saved slicers
        const savedSlicers = getReportSlicers(reportId) ?? [];

        // Filter for native slicers to apply at load time
        // We sanitize them (remove targets) to avoid the TypeError on load
        const nativeSlicers = savedSlicers
          .filter(s => s.visualType?.toLowerCase().includes('slicer'))
          .map(s => ({
            selector: s.selector,
            state: { filters: s.state.filters }
          }));

        if (nativeSlicers.length > 0) {
          console.debug(`2. Applying ${nativeSlicers.length} native slicers via embedConfig.`);
        }

        if (response.data && response.data.accessToken) {
          setEmbedConfig({
            type: 'report',
            embedUrl: response.data.reportConfig[0].embedUrl,
            tokenType: models.TokenType.Embed,
            accessToken: response.data.accessToken,
            settings: {
              persistentFiltersEnabled: false,
              panes: { filters: { expanded: false, visible: false } }
            },
            // Apply native slicers at load
            slicers: nativeSlicers
          });
        } else {
          setError(response.data.errorMsg ?? 'Failed to get embed configuration.');
          setIsLoading(false);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
        setIsLoading(false);
      }
    };
    fetchEmbedConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  /** ---------- Event handlers (changed 'rendered') ---------- */

  const eventHandlers = useMemo(
    () =>
      new Map<string, (event?: service.ICustomEvent<unknown>, embeddedEntity?: Embed) => void | null>([
        ['loaded', () => {
          console.debug("3. Report loaded");
          // Reset one-time flags on each new embed
          appliedOnceRef.current = false;     // <-- reset here

          isApplyingRef.current = false;
          setIsLoading(false);
        }],
        ['rendered', async () => {
          console.debug("4. Report rendered");
          // setIsRendered(true); // Removed

          // If we just reset, skip auto-capture & skip post-render once
          if (isResettingRef.current) {
            console.debug("Reset complete, skipping actions on first render");
            isResettingRef.current = false;
            return;
          }

          // 1) Restore saved slicers post-render (native + Inforiver)
          const rid = reportIdRef.current;
          const rep = reportRef.current;
          if (rid && rep && !appliedOnceRef.current) {
            console.debug("4a. Restore saved slicer post render....")
            appliedOnceRef.current = true;    // mark before applying to avoid re-entrancy
            await applyPostRenderSlicers(rep, rid);
          }

          // 2) After restoration, schedule capture of user changes
          // 2) On subsequent renders, only schedule capture when we're NOT programmatically applying
          if (!isApplyingRef.current) {
            debouncedCapture();
          } else {
            console.debug('4b. Skipping capture after programmatic apply');
          }
        }],
        ['filtersApplied', () => {
          if (!isApplyingRef.current) debouncedCapture();
        }],

        ['error', (event?: service.ICustomEvent<unknown>) => {
          isResettingRef.current = false;
          const detail = event?.detail;
          const message = (detail && typeof detail === 'object' && 'message' in detail)
            ? (detail as { message: string }).message
            : 'An error occurred with the report.';
          setError(message);
          console.error(detail);
          setIsLoading(false);
        }],
      ]),
    [applyPostRenderSlicers, debouncedCapture]
  );

  return (
    <section className="report-area" style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
      {/* Controls */}
      <div className="report-controls">
        <button onClick={handleReset} title="Reset Report" style={{
          background: 'rgba(255, 255, 255, 0.9)', border: '1px solid #ccc', borderRadius: '4px', padding: '6px',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)', color: '#333'
        }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#fff'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'}
        >
          {/* Reset Icon */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>

        <button onClick={() => {
          const elem = document.querySelector('.report-area');
          if (!elem) return;
          if (!document.fullscreenElement) {
            elem.requestFullscreen().catch(err => console.error(`Error enabling full-screen: ${err.message} (${err.name})`));
          } else {
            document.exitFullscreen();
          }
        }} title="Toggle Full Screen" style={{
          background: 'rgba(255, 255, 255, 0.9)', border: '1px solid #ccc', borderRadius: '4px', padding: '6px',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)', color: '#333'
        }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#fff'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'}
        >
          {/* Full Screen Icon */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        </button>
      </div>

      {error ? (
        <div className="report-error-container" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100%', padding: '2rem', textAlign: 'center', color: 'var(--text-primary, #333)'
        }}>
          <div style={{ color: 'var(--error-color, #d32f2f)', marginBottom: '1rem' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: 600 }}>Unable to load report</h3>
          <p style={{ margin: 0, color: 'var(--text-secondary, #666)' }}>{error}</p>
        </div>
      ) : (
        <>
          {isLoading && (
            <div className="report-spinner-container">
              <div className="report-loading-spinner"></div>
              <div className="report-spinner-text">Loading report...</div>
            </div>
          )}

          <PowerBIEmbed
            key={embedKey}
            embedConfig={embedConfig}
            eventHandlers={eventHandlers}
            cssClassName={`report-container`}
            getEmbeddedComponent={(embeddedReport: Embed) => {
              const reportInstance = embeddedReport as Report;
              // setReport(reportInstance); // Removed
              reportRef.current = reportInstance;

              // Bonus: wire error logging immediately
              reportInstance.on('error', (errorObject) => {
                const err = (errorObject as service.ICustomEvent<models.IError>)?.detail;
                console.log(`Power BI error: ${err?.message}; details: ${err?.detailedMessage}`);
                console.log(err);
              });
            }}
          />
        </>
      )}
    </section>
  );
};

export default ReportEmbed;